import {useMemo} from "react";

import {GreenClaimLanguage} from "~/constants/greenClaims";
import {findGreenClaims, summarizeGreenClaims, IGreenClaimSummary} from "~/utils/greenClaims";
import {htmlToPlainText} from "~/utils/htmlToPlainText";

const TEXT_SEPARATOR = "\u0000";
const BLOCK_BOUNDARY_TAG = /<\/(p|div|li|ul|ol|h[1-6]|blockquote)>|<br\s*\/?>/gi;

export interface IUseGreenClaimsProps {
  languages: GreenClaimLanguage[];
  plainTexts?: (string | null | undefined)[];
  richTextValues?: (string | null | undefined)[];
}

const getRichTextPlainText = (value: string): string => {
  if (!value) return "";

  try {
    const {htmlDescription} = JSON.parse(value);
    if (!htmlDescription) return "";

    return htmlToPlainText(htmlDescription.replace(BLOCK_BOUNDARY_TAG, "$& "));
  } catch {
    return "";
  }
};

export const useGreenClaims = ({
  languages,
  plainTexts = [],
  richTextValues = [],
}: IUseGreenClaimsProps): IGreenClaimSummary => {
  const languagesKey = languages.join(",");
  const plainTextKey = plainTexts.filter(Boolean).join(TEXT_SEPARATOR);
  const richTextKey = richTextValues.filter(Boolean).join(TEXT_SEPARATOR);

  return useMemo(() => {
    const texts = [plainTextKey, ...richTextKey.split(TEXT_SEPARATOR).map(getRichTextPlainText)];
    const matches = texts.flatMap((text) => findGreenClaims(text, languages));

    return summarizeGreenClaims(matches);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languagesKey, plainTextKey, richTextKey]);
};
